import { jsxs, jsx } from 'react/jsx-runtime';
import { Children, Fragment, useContext, createContext } from 'react';
import { twMerge } from 'tailwind-merge';
import Avatar from '../../DataDisplays/Avatar/Avatar.js';
import Typography from '../../Base/Typography/Typography.js';
import Box from '../../Base/Layouts/Box/Box.js';
import '../../Base/Layouts/FullBleed/FullBleed.js';
import NewBadge from '../../DataDisplays/NewBadge/NewBadge.js';
import Divider from '../../DataDisplays/Divider/Divider.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const GNBListContext = /*#__PURE__*/ createContext({
    type: ''
});
/**
 * GNB 리스트의 루트 컴포넌트
 * @param {Object} props
 * @param {'main'|'sub'|'user'} [props.type] - 리스트 타입 (main, sub, user)
 * @param {string} [props.className] - 추가적인 CSS 클래스
 * @param {ReactNode} props.children - 리스트 아이템들
 */ const Root = ({ type = 'main', children, className })=>{
    const childrenWithDivider = Children.map(children, (child, index)=>{
        const isFirstChild = index === 0;
        return isFirstChild ? child : /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(Divider, {
                    className: "my-2"
                }),
                child
            ]
        });
    });
    return /*#__PURE__*/ jsxs(GNBListContext.Provider, {
        value: {
            type
        },
        children: [
            type === 'main' && /*#__PURE__*/ jsxs(Box, {
                className: twMerge('absolute flex h-auto w-[200px] overflow-hidden rounded-xl bg-w-white shadow-graymedium', className),
                "data-testid": "design-system-gnb-list--root-main",
                children: [
                    /*#__PURE__*/ jsx(Box, {
                        className: "m-0 h-auto w-2 bg-gradient-primary-45 p-0",
                        "data-testid": "design-system-gnb-list--root-main-sidebar"
                    }),
                    /*#__PURE__*/ jsx(Box, {
                        className: "w-full py-[13px]",
                        children: childrenWithDivider
                    })
                ]
            }),
            type === 'sub' && /*#__PURE__*/ jsx(Box, {
                className: twMerge('absolute flex h-auto w-[200px] flex-col overflow-hidden rounded-xl bg-w-white py-[13px] shadow-graymedium ', className),
                "data-testid": "design-system-gnb-list--root-sub",
                children: childrenWithDivider
            }),
            type === 'user' && /*#__PURE__*/ jsx(Box, {
                className: twMerge('absolute flex w-full flex-col bg-w-white pb-[14px] pt-[22px] desktop:w-[280px] desktop:rounded-xl desktop:shadow-graymedium', className),
                "data-testid": "design-system-gnb-list--root-user",
                children: childrenWithDivider
            })
        ]
    });
};
/**
 * GNB 리스트의 기본 리스트 컴포넌트
 * @param {Object} props
 * @param {ReactNode} props.children - 리스트 아이템들
 * @param {string} [props.className] - 추가적인 CSS 클래스
 */ const UserInfo = ({ imgUrl, userId, email, children, onClick })=>{
    return /*#__PURE__*/ jsxs(Box, {
        className: "flex w-full flex-col gap-4 px-6 pb-4",
        children: [
            /*#__PURE__*/ jsxs(Box, {
                className: "flex w-full items-center gap-4",
                children: [
                    /*#__PURE__*/ jsx(Avatar, {
                        size: "lg",
                        isEditable: true,
                        imgUrl: imgUrl,
                        onClick: onClick
                    }),
                    /*#__PURE__*/ jsxs(Box, {
                        className: "flex w-full flex-col gap-0.5",
                        children: [
                            /*#__PURE__*/ jsx(Typography, {
                                variant: "body1",
                                className: "font-medium text-w-gray-900",
                                children: userId
                            }),
                            /*#__PURE__*/ jsx(Typography, {
                                variant: "body2",
                                className: "line-clamp-2 break-all text-w-gray-600",
                                children: email
                            })
                        ]
                    })
                ]
            }),
            children && /*#__PURE__*/ jsx(Box, {
                className: "flex w-full flex-col gap-2",
                children: children
            })
        ]
    });
};
/**
 * GNB 리스트의 기본 리스트 컴포넌트
 * @param {Object} props
 * @param {ReactNode} props.children - 리스트 아이템들
 * @param {string} [props.className] - 추가적인 CSS 클래스
 */ const List = ({ children })=>{
    return /*#__PURE__*/ jsx(Box, {
        className: "w-full text-w-gray-900",
        "data-testid": "design-system-gnb-list--list",
        children: children
    });
};
/**
 * GNB 리스트의 서브 리스트 컴포넌트
 * @param {Object} props
 * @param {ReactNode} props.children - 서브 리스트 아이템들
 * @param {string} [props.className] - 추가적인 CSS 클래스
 */ const SubList = ({ children })=>{
    return /*#__PURE__*/ jsx(Box, {
        className: "w-full text-w-gray-600",
        "data-testid": "design-system-gnb-list--sub-list",
        children: children
    });
};
/**
 * GNB 리스트의 아이템 컴포넌트
 * @param {Object} props
 * @param {ReactNode} props.children - 아이템 컨텐츠
 * @param {string} [props.className] - 추가적인 CSS 클래스
 * @param {() => void} [props.onClick] - 클릭 핸들러
 * @param {boolean} [props.isSelected] - 선택 상태 여부
 */ const Item = (_param)=>{
    var { hasNew = false, children, className, textClassName } = _param, rest = _object_without_properties(_param, [
        "hasNew",
        "children",
        "className",
        "textClassName"
    ]);
    const { type } = useContext(GNBListContext);
    return /*#__PURE__*/ jsxs(Box, _object_spread_props(_object_spread({
        className: twMerge('flex w-full cursor-pointer gap-1 px-4 py-3 hover:bg-primary-10', type === 'user' && 'px-6', className),
        "data-testid": `design-system-gnb-list--${type}-item-${children}`
    }, rest), {
        children: [
            /*#__PURE__*/ jsx(Typography, {
                variant: "body1",
                className: textClassName,
                "data-testid": `design-system-gnb-list--${type}-item-text-${children}`,
                children: children
            }),
            hasNew && /*#__PURE__*/ jsx(NewBadge, {
                className: "mt-1"
            })
        ]
    }));
};

export { Item, List, Root, SubList, UserInfo };
